import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar, Footer } from "@/components/yc/Nav";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";

const formatDetail = (detail) => {
    if (!detail) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
};

const Login = () => {
    const { user, refresh } = useAuth();
    const [search] = useSearchParams();
    const intentRole = search.get("role"); // brand | creator
    const navigate = useNavigate();
    const [mode, setMode] = useState("login"); // login | register
    const [form, setForm] = useState({ email: "", password: "", name: "" });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (user && user.role !== "unset") navigate(`/${user.role}/dashboard`);
        if (user && user.role === "unset") navigate(`/role${intentRole ? `?role=${intentRole}` : ""}`);
    }, [user, intentRole, navigate]);

    const handleGoogle = () => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const state = (window.crypto?.randomUUID && window.crypto.randomUUID()) || Math.random().toString(36).slice(2);
        sessionStorage.setItem("cc_oauth_state", state);
        if (intentRole) sessionStorage.setItem("cc_intent_role", intentRole);
        else sessionStorage.removeItem("cc_intent_role");
        const redirectUri = window.location.origin + "/auth/google";
        const params = new URLSearchParams({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            redirect_uri: redirectUri,
            response_type: "code",
            scope: "openid email profile",
            state,
            prompt: "select_account",
        });
        window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.email.trim() || !form.password) { toast.error("Enter your email and password"); return; }
        if (mode === "register" && form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
        if (intentRole) sessionStorage.setItem("cc_intent_role", intentRole);
        setBusy(true);
        try {
            const path = mode === "register" ? "/auth/register" : "/auth/login";
            await api.post(path, form);
            await refresh(); // load /auth/me; the effect above then routes to /role or dashboard
        } catch (err) {
            toast.error(formatDetail(err.response?.data?.detail));
        } finally { setBusy(false); }
    };

    const title = mode === "register"
        ? (intentRole === "creator" ? "Apply as a creator" : intentRole === "brand" ? "Join as a brand" : "Create your account")
        : "Sign in";

    return (
        <div className="min-h-screen bg-yc-cream flex flex-col">
            <Navbar />
            <section className="flex-1 flex items-center justify-center px-6 py-16">
                <div className="yc-card p-8 sm:p-10 max-w-md w-full">
                    <div className="yc-overline">Welcome</div>
                    <h1 className="font-heading text-4xl font-black tracking-tighter mt-3">{title}</h1>
                    <p className="text-sm text-yc-ink/70 mt-3">
                        {mode === "register" ? "Create an account with your email, or continue with Google." : "Sign in with your email and password, or continue with Google."}
                    </p>

                    <button data-testid="login-google-btn" onClick={handleGoogle} className="yc-btn-primary w-full mt-6 gap-3">
                        <svg viewBox="0 0 48 48" className="w-5 h-5 mr-2"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.1 8.1 3l5.7-5.7C34.5 6 29.5 4 24 4 12.95 4 4 12.95 4 24s8.95 20 20 20 20-8.95 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3.1 0 5.9 1.1 8.1 3l5.7-5.7C34.5 6 29.5 4 24 4 16.3 4 9.6 8.4 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.3l-6.3-5.2C29.2 35.2 26.7 36 24 36c-5.1 0-9.5-3.3-11.2-7.9l-6.5 5C9.5 39.5 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.4 4.2-4.4 5.5l6.3 5.2C40.4 35.4 44 30.1 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
                        Continue with Google
                    </button>

                    <div className="flex items-center gap-3 my-6">
                        <div className="h-px flex-1 bg-yc-ink/15" />
                        <span className="text-xs text-yc-ink/50 uppercase tracking-wider">or</span>
                        <div className="h-px flex-1 bg-yc-ink/15" />
                    </div>

                    <form onSubmit={submit} className="space-y-3" data-testid="email-auth-form">
                        {mode === "register" && (
                            <div>
                                <label className="yc-overline">Name</label>
                                <input data-testid="auth-name" className="yc-input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" />
                            </div>
                        )}
                        <div>
                            <label className="yc-overline">Email</label>
                            <input data-testid="auth-email" type="email" className="yc-input mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
                        </div>
                        <div>
                            <label className="yc-overline">Password</label>
                            <input data-testid="auth-password" type="password" className="yc-input mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={mode === "register" ? "At least 8 characters" : "Your password"} />
                        </div>
                        <button data-testid="email-auth-submit" type="submit" disabled={busy} className="yc-btn-secondary w-full mt-2 disabled:opacity-50">
                            {busy ? "Please wait…" : mode === "register" ? "Create account" : "Sign in"}
                        </button>
                    </form>

                    <div className="text-center text-sm text-yc-ink/70 mt-5">
                        {mode === "register" ? (
                            <>Already have an account?{" "}
                                <button data-testid="switch-to-login" onClick={() => setMode("login")} className="text-yc-blue font-semibold underline">Sign in</button>
                            </>
                        ) : (
                            <>No account yet?{" "}
                                <button data-testid="switch-to-register" onClick={() => setMode("register")} className="text-yc-blue font-semibold underline">Create one</button>
                            </>
                        )}
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
};

export default Login;
